import { FormValueScope } from '@epages/react-components'
import { kebabCase } from 'lodash-es'
import { useSelector } from 'react-redux'
import cc from 'classcat'

import type { SwitchTabFunction } from './HeaderSettings'
import { useTranslate, withComponents } from '../../utils/translate'
import FormGroup from '../formComponents/FormGroup'
import FormValue from '../formComponents/FormValue'
import useHelpCenterLink from '../../utils/hooks/useHelpCenterLink'

type Props = {
  switchTab: SwitchTabFunction
}

export default function MenuTypeSettings({ switchTab }: Readonly<Props>) {
  const t = useTranslate('interface', 'components.bottomBar.headerSettings.headerMenu')

  const helpCenterLink = useHelpCenterLink('MENU_TYPE')

  // We know that every theme has a theme settings group "themeHeader" with a radio buttons setting "menuType".
  const themeMenuTypeSetting = useSelector<State, Theme.RadioButtonsSettingsSchemaItem>(
    (state) =>
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      (state.get('themeSettingsSchema').toJS() as Theme.SettingsSchema)
        .find((group) => group.name === 'themeHeader')!
        .settings.find((item) => item.name === 'menuType') as Theme.RadioButtonsSettingsSchemaItem,
  )
  const selectedHeaderType: ThemeHeaderTypes = useSelector<State, ThemeHeaderTypes>((state) =>
    state.getIn(['shop', 'themeSettings', 'themeHeader', 'headerType']),
  )
  const disableMenuTypeSelection = selectedHeaderType === 'oneRow'

  return (
    <>
      <div className="dali-settingslayer-element small">
        {withComponents(t)('description', {
          link: (
            <a
              className="ep-form-row-text-external-link"
              href={helpCenterLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('helpCenterLink')}
            </a>
          ),
        })}
      </div>
      {disableMenuTypeSelection && (
        <div className="dali-settingslayer-element">
          <p className="ep-notification-info">
            {withComponents(t)('disabledMenuInfoBox.description', {
              link: (
                <button
                  type="button"
                  className="ep-simple-notification-button"
                  onClick={() => switchTab('themeHeader')}
                >
                  {t('disabledMenuInfoBox.link')}
                </button>
              ),
            })}
          </p>
        </div>
      )}
      <div className="dali-settingslayer-element">
        <FormValueScope name="themeHeader">
          <FormValue name="menuType">
            {(headerMenu: string, setHeaderMenu: (data: string) => void) =>
              themeMenuTypeSetting.options.map((menuType) => (
                <div
                  key={menuType}
                  className={cc(['ep-form-row ep-menu-type-settings', disableMenuTypeSelection && 'disabled'])}
                >
                  <FormGroup
                    name="menuType"
                    key={menuType}
                    className="ep-menu-type-settings-form"
                    label={t(`menuTypes.${menuType}.label`)}
                    info={t(`menuTypes.${menuType}.tooltip`)}
                    tagName="label"
                    labelAfterComponent
                  >
                    <input
                      type="radio"
                      name="menuType"
                      value={menuType}
                      disabled={disableMenuTypeSelection}
                      checked={headerMenu === menuType}
                      onChange={() => setHeaderMenu(menuType)}
                    />
                  </FormGroup>
                  <div
                    className={cc(['ep-menu-type-scheme', [`ep-menu-type-scheme-${kebabCase(menuType)}`]])}
                    onClick={() => {
                      if (!disableMenuTypeSelection) {
                        setHeaderMenu(menuType)
                      }
                    }}
                  />
                </div>
              ))
            }
          </FormValue>
        </FormValueScope>
      </div>
    </>
  )
}

MenuTypeSettings.defaultValue = {}
